/*
 * Shared API types for the Mesh Console.
 * These bind to what the running Transport sidecar actually returns, plus
 * the namespaced surfaces for planes whose services are NOT up yet.
 */

/**
 * The five planes plus the synthesised Overview, the Operator Cockpit, and the
 * terminal-free Done-gate (the SEAL-LOOP / step-9 visualizer).
 */
export type PlaneId =
  | "overview"
  | "cockpit"
  | "donegate"
  | "transport"
  | "trust"
  | "catalog"
  | "observability"
  | "consent";

/** Operator chrome roles. Drives role-aware affordances. */
export type Role = "operator" | "auditor" | "exec";

/* ---------- Health ---------- */

export type HealthProbe = "live" | "ready" | "startup";

export interface HealthState {
  /** True only when /healthz returned 200. The green-dot gate. */
  healthy: boolean;
  /** HTTP status actually observed, or 0 on network failure. */
  status: number;
  /** Raw probe results when fetched individually. */
  probes?: Partial<Record<HealthProbe, boolean>>;
  /** Wall-clock of the observation. */
  checkedAt: number;
  /** Round-trip latency in ms, when measurable. */
  latencyMs?: number;
  /** Populated on failure for the loud offline state. */
  error?: string;
}

/* ---------- Dispatch ---------- */

/**
 * A dispatch receipt — HTTP 202 body from POST /v1/dispatch and the shape
 * returned by GET /v1/dispatch/{request_id}.
 *
 * `audit_ack_hash` stays ALL-ZEROS until store-and-forward delivery completes.
 * Model "queued vs delivered" honestly from that: see `isDelivered()`.
 */
export interface DispatchReceipt {
  request_id: string;
  status: string;
  signature_verified: boolean;
  /** Hash of the opening audit-pair entry (always present once accepted). */
  audit_open_hash: string;
  /** Hash of the ACK audit entry. All-zeros => still queued, not yet delivered. */
  audit_ack_hash: string;
}

/** The all-zeros sentinel for an un-acked audit hash (64 hex chars). */
export const ZERO_HASH = "0".repeat(64);

/** True only when store-and-forward delivery has produced a real ACK hash. */
export function isDelivered(r: Pick<DispatchReceipt, "audit_ack_hash">): boolean {
  const h = (r.audit_ack_hash ?? "").replace(/^0x/, "");
  return h.length > 0 && !/^0+$/.test(h);
}

/* ---------- Metrics ---------- */

/** One parsed Prometheus sample. */
export interface MetricSample {
  name: string;
  labels: Record<string, string>;
  value: number;
}

/** A point in a client-built time series (metrics are poll-derived). */
export interface MetricPoint {
  t: number;
  value: number;
}

/** The transport metric series names the sidecar exposes. */
export const TRANSPORT_METRICS = {
  requestsTotal: "mesh_transport_requests_total",
  signatureVerifications: "mesh_transport_signature_verifications_total",
  queuePending: "mesh_transport_queue_pending",
  replayDetections: "mesh_transport_replay_detections_total",
  deadLetters: "mesh_transport_dead_letters_total",
  auditPairOrphans: "mesh_transport_audit_pair_orphans_total",
  peerKeyLookups: "mesh_transport_peer_key_lookups_total",
  latencyBucket: "mesh_transport_latency_seconds_bucket",
  latencyCount: "mesh_transport_latency_seconds_count",
  latencySum: "mesh_transport_latency_seconds_sum",
} as const;

/* ---------- Provability ---------- */

/**
 * A claim + the evidence that substantiates it. The ProvabilityDrawer renders
 * this as a "receipt". A claim with no evidence MUST render as unsubstantiated
 * (the UI never shows a trust badge it cannot back from live data).
 */
export interface Evidence {
  /** Hashes to display in mono, optionally each linking to an audit entry. */
  hashes?: { label: string; value: string; auditEntry?: string }[];
  /** The RFC 9421 signature base (the exact bytes that were signed). */
  signatureBase?: string;
  /** RFC 7638 JWK thumbprint of the signing key. NEVER the key material. */
  keyThumbprint?: string;
  /** The signature algorithm, e.g. "ed25519". */
  algorithm?: string;
  /** Spec section reference, e.g. "Part IV §27.3". */
  specRef?: string;
  /** Free-form mono detail lines (covered headers, created/expires, etc). */
  detail?: { label: string; value: string }[];
  /** Source URL the claim was read from (a metric → its /v1/metrics line). */
  source?: string;
}

export interface Claim {
  /** Short human label of what is being asserted. */
  label: string;
  /** The verification verdict that drives badge colour. */
  verdict: VerifyState;
  evidence?: Evidence;
}

/** Badge / verification states. Maps to semantic colour. */
export type VerifyState =
  | "verified" // teal — proven from live data
  | "attested" // gold — attested but not independently re-verified here
  | "failed" // red — verification ran and FAILED (loud)
  | "offline" // red/grey — plane offline, cannot substantiate
  | "pending" // ember — in flight / queued
  | "unknown"; // grey — no evidence supplied
